<script lang="ts">
    import { ServiceConsts } from "$/services/ServiceConsts";
    import { AppStore, AppType } from "$/stores/AppStore";
    import { TenantStore } from "$/stores/TenantStore";
    import {
        getEffectivePricingPlan,
        isElementOverflown,
        isEmail,
        isExtensionOrPdfViewer,
    } from "$/utils/Utils";
    import slackFavicon from "$/assets/images/slack-icon.png";
    import { removeSuggestionList} from "../../utils";
    import SvgIcon from "$/components/SVGIcon.svelte";
    import { ActiveTopicStore, TopicAccessLevel } from "$/stores/extension/ActiveTopicStore";
    import CollaboratorItem from "$/components/CollaboratorItem.svelte";
    import { get } from "svelte/store";
    import { isMobile } from "$/utils/isMobile";

    // (ELEMENT_HEIGHT + 4px (element padding bottom)) * 3 + 32px (height of the category title) + 4px (element padding bottom)
    const WRAPPER_HEIGHT =
        get(isMobile) || isExtensionOrPdfViewer() ? 175 : 192;
    const ELEMENT_HEIGHT = 48;

    export let props;
    export let userRole;
    export let mentions;

    let selectedIndex = 0;
    let element;
    let mentionWithEmail = false;
    let userCanMention = false;
    let suggestionOptionRefs = [];

    $: userGroupedByWorkspaces = TenantStore.groupCollaboratorsByWorkspace(props.items);
    $: effectivePricingPlan = getEffectivePricingPlan($TenantStore?.active);
    $: userRole, effectivePricingPlan, checkUserCanMention();
    $: slackUsersGroupedByWorkspaces = userGroupedByWorkspaces.filter(
        (el) => el.id,
    );
    $: topicMembers = userGroupedByWorkspaces.find((el) => !el.id);
    $: slackSugestions = slackUsersGroupedByWorkspaces
        .map((el) => el.members.sort(sortMembersAlphabetically))
        .flat();
    $: element, calculateInitialSelectedIndex();
    $: topicSugestions = topicMembers
        ? topicMembers.members.sort(sortMembersAlphabetically)
        : [];
    $: suggestionsCount = slackSugestions.length + topicSugestions.length;
    $: props?.query, checkForEmailMention();

    function calculateInitialSelectedIndex() {
        if (!element) return;
        requestAnimationFrame(() => {
            element.scrollTo({
                top: -(element.scrollHeight - element.clientHeight),
                behavior: "instant",
            });
        });
    }

    function sortMembersAlphabetically(a, b) {
        return (a.firstName + a.lastName + a.username).localeCompare(
            b.firstName + b.lastName + b.username,
        );
    }

    function handleUpgradeClicked() {
        if ($AppStore.type === AppType.DASHBOARD) {
            // suggestion vindow has no access to routify context, so we need to use history api
            // if we were to merge this I would create a ticket to further analyze this and replace with routify
            history.pushState(null, "", "/payment");
        } else {
            window.open(
                `${ServiceConsts.DASHBOARD_URL}/#/${$TenantStore?.active?.id}/payment`,
                "_blank",
            );
        }
    }

    function checkUserCanMention() {
        // non-owners can not mention
        if (!userRole || userRole < TopicAccessLevel.OWNER) {
            userCanMention = false;
            return;
        }

        if (effectivePricingPlan === 0) {
            userCanMention = true;
            return;
        }

        // expired plan can not mention
        if (effectivePricingPlan === -1) {
            userCanMention = false;
            return;
        }

        // paid plans
        if (effectivePricingPlan >= 1) {
            userCanMention = true;
            return;
        }
    }

    export const onKeyDown = ({ event }: { event: KeyboardEvent }) => {

        if (event.key === "ArrowUp") {
            navigationHandler("up");
            return true;
        }

        if (event.key === "ArrowDown") {
            navigationHandler("down");
            return true;
        }

        if (event.key === "Enter") {
            enterHandler();
            return true;
        }

        if (event.key === "Escape") {
            removeSuggestionList();
            return true;
        }

        return false;
    };

    const navigationHandler = (direction: "up" | "down") => {
        const difference = direction === "down" ? 1 : -1;
        selectedIndex =
            (selectedIndex + suggestionsCount + difference) % suggestionsCount;
        scrollSuggestionMenu(selectedIndex);
    };

    const enterHandler = () => {
        // Check if the selected suggestion is an upgrade suggestion
        const isExistingUserMention = [...topicSugestions, ...slackSugestions][
            selectedIndex
        ];
        
        onSelect(isExistingUserMention);
    };

    const scrollSuggestionMenu = (index: number) => {
        if (!element) return;

        const suggestionElement = suggestionOptionRefs[index];

        if (suggestionElement && isElementOverflown(suggestionElement)) {
            suggestionElement.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            });
        }
    };

    const onSelect = (collaborator) => {
        if (
            collaborator &&
            collaborator.workspaceId &&
            !collaborator.isMember &&
            (userRole < TopicAccessLevel.EDITOR)
        ) {
            props.command({ ...collaborator, role: TopicAccessLevel.MENTION });
        } else if (ActiveTopicStore.isInbox() && collaborator) {
            props.command({ id: null, username: collaborator.username });
        } else if (collaborator) {
             props.command(collaborator);
        } else if (userCanMention && mentionWithEmail) {
            props.command({ id: null, username: props.query });
        }
    };

    const calculateSlackSuggestionIndex = (workspaceIndex) => {
        let suggestionIndex = 0;
        for (let i = 0; i < workspaceIndex; i++)
            suggestionIndex +=
                slackUsersGroupedByWorkspaces[i]?.members?.length;

        return topicSugestions.length + suggestionIndex;
    };

    function checkForEmailMention() {
        if (
            userCanMention &&
            props.query != "" &&
            props.items.length === 0 &&
            isEmail(props.query)
        ) {
            mentionWithEmail = true;
        } else {
            mentionWithEmail = false;
        }
    }

    function getAdditionalInfo() {
        return userCanMention
            ? "Enter full email address to invite new collaborator"
            : "Try searching by user name or email address";
    }
</script>

<div class="suggestion-wrapper">
    {#if mentionWithEmail}
        <button
            class="create-collaborator-button active"
            on:click={() => onSelect(0)}>Email: <b>{props.query}</b></button
        >
    {:else if suggestionsCount === 0}
        <div class="no-results-message">
            <span class="no-results-message__additional-info">
                {getAdditionalInfo()}
            </span>
        </div>
    {:else}
        <div
            class="results flex-col-reverse"
            style="max-height:{WRAPPER_HEIGHT}px"
            bind:this={element}
        >
            {#if slackSugestions.length}
                <div>
                {#key slackUsersGroupedByWorkspaces}
                    {#each slackUsersGroupedByWorkspaces as workspace, workspaceIndex}
                        <div class="suggestion-workspace-header">
                            <p class="workspace-slack-icon">
                                <img
                                        src={workspace.icon}
                                        class="workspace-icon"
                                        alt="{workspace.name} slack icon"
                                />
                                <img
                                        src={slackFavicon}
                                        class="slack-icon"
                                        alt="slack logo"
                                />
                            </p>
                            <p class="workspace-title">
                                {workspace.name}
                                <span class="suggestion-count"
                                >{workspace.members.length}</span
                                >
                            </p>
                        </div>
                        {#each workspace.members.sort(sortMembersAlphabetically) as collaborator, index}
                            <CollaboratorItem
                                    {collaborator}
                                    {selectedIndex}
                                    {index}
                                    {workspaceIndex}
                                    {onSelect}
                                    {ELEMENT_HEIGHT}
                                    {calculateSlackSuggestionIndex}
                                    bind:refElement={suggestionOptionRefs[
                                        index +
                                            calculateSlackSuggestionIndex(
                                                workspaceIndex,
                                            )
                                    ]}
                            />
                        {/each}
                    {/each}
                {/key}
                </div>
            {/if}

            {#if topicSugestions.length}
                <div>
                {#if topicSugestions.length}
                    <div class="suggestion-workspace-header">
                        <p class="workspace-slack-icon">
                            <SvgIcon name="multiple-users" size={16} />
                        </p>
                        <p class="workspace-title">
                            {#if ActiveTopicStore.isInbox()}
                                Collaborators
                            {:else}
                                Topic & Cluster Members
                            {/if}
                            <span class="suggestion-count"
                            >{topicSugestions.length}</span
                            >
                        </p>
                    </div>
                {/if}
                {#each topicSugestions as collaborator, currentCounter}
                <CollaboratorItem
                  {collaborator}
                  {selectedIndex}
                  {currentCounter}
                  {ELEMENT_HEIGHT}
                  index={undefined}
                  workspaceIndex={undefined}
                  calculateSlackSuggestionIndex={undefined}
                  bind:refElement={suggestionOptionRefs[currentCounter]}
                  {onSelect}
                />
                {/each}
                </div>
            {/if}


        </div>
    {/if}
</div>

<style>
    .suggestion-wrapper {
        @apply relative h-[calc(100%-10px)] my-1 overflow-y-auto overflow-x-hidden rounded-md border border-solid border-gray-200 bg-white p-1 pb-0 text-black shadow !important;
    }

    .results {
        @apply flex h-full w-full flex-col-reverse overflow-y-auto overflow-x-hidden !important;
    }

    .results::-webkit-scrollbar-thumb {
        @apply rounded-full border-l-2 border-solid border-transparent bg-clip-content !important;
    }

    .create-collaborator-button {
        @apply mb-1 w-full cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap border-none p-2 text-left text-black !important;
    }

    .create-collaborator-button.active {
        @apply border-none bg-gray-100 text-sm !important;
    }

    .no-results-container {
        @apply flex flex-col w-full h-full items-center justify-center gap-4 pb-2;
    }

    .no-results-message {
        @apply flex h-auto flex-col items-center gap-1 p-2 text-center text-sm !important;
    }

    .no-results-message__info {
        @apply font-semibold !important;
    }

    .no-results-message__additional-info {
        @apply text-gray-500  text-sm !important;
    }
    .upgrade-button {
        @apply reset rounded-full bg-yellow-300 px-4 py-2 text-xs tracking-tight text-black transition-all hover:scale-105 hover:bg-yellow-400 !important;
    }
    .suggestion-section-header {
        @apply m-0 flex flex-row items-center rounded-md bg-gray-200 px-3 py-1.5 text-sm !important;
    }

    .suggestion-workspace-header {
        @apply sticky top-0 m-0 mb-1 flex flex-row items-center rounded-md px-2 py-1 text-sm !important;
        background-color: #e5e7eb !important;
        color: #717886 !important;
    }

    .workspace-title {
        @apply m-0 ml-1.5 flex w-full gap-1 text-sm !important;
    }

    .workspace-icon {
        @apply h-4 w-4 rounded-full align-middle !important;
    }

    .slack-icon {
        @apply absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-white !important;
    }

    .workspace-slack-icon {
        @apply relative m-0 flex !important;
    }

    .suggestion-count {
        @apply ml-auto flex h-5 min-w-5 items-center justify-center rounded-sm p-0.5 text-xs text-gray-400 hover:bg-gray-300 hover:text-gray-500 !important;
    }

    .slack-favicon {
        @apply ml-1 h-4 w-4 !important;
    }

    .close-button {
        @apply reset absolute right-1 top-1 text-gray-500 !important;
    }

    .workspace-name {
        @apply ml-1 flex-shrink-0 text-gray-400 !important;
    }
</style>
